import {
  FunctionComponent,
  useState,
  useEffect,
  useCallback,
  Fragment,
} from 'react'
import { useParams } from 'react-router'

import LoadingPage from '@/components/LoadingPage'
import { LightHeader } from '../../components/LightHeader'
import { ForgotPasswordForm } from '../../components/ForgotPasswordForm'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import TimeZoneLuxon from '@/components/TimeZoneLuxon'
import { getThemeByUniqueCode } from '@/theme'
import { getThemeByUniqueCode as getRebrandingThemeByUniqueCode } from '@/rebrandedTheme'
import { setCache } from '@/utils/cache'
import { useStyles } from './styles'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { RenderIf } from '@/components/RenderIf'
import { useRebrandingThemeGeneratorContext } from '@/rebrandedTheme/contexts/ThemeGeneratorContext'
import { ForgotPasswordParams } from './types'
import { Text } from '@conexasaude/conexa-hero'

const ForgotPassword: FunctionComponent = () => {
  const [isLoading, setIsLoading] = useState(true)
  const { clinic } = useParams<ForgotPasswordParams>()
  const { theme, setThemeColors } = useThemeGeneratorContext()
  const { theme: rebrandingTheme, setThemeColors: setRebrandingThemeColors } =
    useRebrandingThemeGeneratorContext()
  const styles = useStyles()
  const { user } = useAuthContext()

  const getAndSetThemeColors = useCallback(async () => {
    try {
      const colors = await getThemeByUniqueCode(clinic, theme)

      setThemeColors(colors)
    } catch {
      setThemeColors(null)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinic])

  const getAndSetRebrandingThemeColors = useCallback(async () => {
    try {
      const colors = await getRebrandingThemeByUniqueCode(
        clinic,
        rebrandingTheme,
      )

      setRebrandingThemeColors(colors)
    } catch {
      setRebrandingThemeColors(null)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinic])

  useEffect(() => {
    setIsLoading(true)

    if (clinic) {
      getAndSetThemeColors()
      getAndSetRebrandingThemeColors()
      setCache('unique-code', clinic)
      setIsLoading(false)

      return
    }

    setThemeColors(null)
    setRebrandingThemeColors(null)
    setCache('unique-code', '')
    setIsLoading(false)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isLoading) return <LoadingPage withoutNav />

  return (
    <Fragment>
      <LightHeader
        uniqueCode={clinic}
        redirectTo={clinic ? `/login/${clinic}` : '/login'}
      />
      <div css={styles.container}>
        <ForgotPasswordForm />
        <div css={styles.tzContainer}>
          <TimeZoneLuxon />
          <RenderIf condition={Boolean(user?.patientId)}>
            <Text>
              ID: {user?.patientId} | Clínica: {user?.clinic?.name}
            </Text>
          </RenderIf>
        </div>
      </div>
    </Fragment>
  )
}

export default ForgotPassword
